import { bySortOrder } from "./theme.js";
/**
 * 손님이 고르는 동작 수 — **최소 1개, 최대 2개**(2026-10-15: 캔버스는 "두 개" 고정이었으나 하나만 골라도 진행할 수 있게 완화).
 * 서버 검증(`parseMotionIds`·`startVideoJob`)·키오스크 CTA 활성·콘솔 테스트가 공유한다.
 * `motionIds` 생략(undefined) 은 구 키오스크 호환 모드(동작 없이 기본 프롬프트) 로 별개 — 빈 배열은 거부.
 */
export const MOTION_PICK_MIN = 1;
export const MOTION_PICK_MAX = 2;
/** 고른 동작 수가 허용 범위(MIN ≤ n ≤ MAX) 인지. */
export function isValidMotionPickCount(count) {
    return Number.isInteger(count) && count >= MOTION_PICK_MIN && count <= MOTION_PICK_MAX;
}
/** 스타일이 동작을 따로 지정하지 않았는지 — `motionIds` 없음/빈 배열 = 활성 동작 전체(기본). 콘솔 카드 배지·모달 모드·리졸버가 공유. */
export function styleUsesAllMotions(style) {
    return !style.motionIds || style.motionIds.length === 0;
}
/**
 * 스타일에 노출할 동작 (BOOTH-86).
 * - `style.motionIds` 없음/빈 배열 → 활성 동작 전체, 전역 sortOrder 순.
 * - 있으면 그 순서대로 활성 동작만 — 없는 id·비활성 id·중복 id(첫 번째만)는 건너뛴다.
 * 입력 배열은 변경하지 않는다. 호출부는 보통 full `AIStyle` 을 넘긴다.
 */
export function resolveMotionsForStyle(motions, style) {
    const enabled = motions.filter((m) => m.isEnabled);
    if (styleUsesAllMotions(style))
        return [...enabled].sort(bySortOrder);
    const byId = new Map(enabled.map((m) => [m.id, m]));
    const out = [];
    const seen = new Set();
    for (const id of style.motionIds) {
        if (seen.has(id))
            continue;
        seen.add(id);
        const m = byId.get(id);
        if (m)
            out.push(m);
    }
    return out;
}
/** 동작 미리보기 영상 저장소 키 prefix — 세션 영상(`v/{code}.mp4`)·소개 샘플(`v/samples/`) 과 같은 `/v/*` 경로 아래 별도 폴더. */
export const MOTION_PREVIEW_KEY_PREFIX = "v/motions/";
/**
 * 동작 미리보기 영상의 공급자별 비율 — 키오스크·콘솔 동작 타일(300×200, 3:2 가로) 을 cover 로 채우는 가로 영상.
 * - google(Veo): 9:16·16:9 만 지원 → 16:9. 3:2 썸네일을 넣으면 Veo 가 좌우를 검은 띠로 채우고 타일 cover 가 띠를 잘라낸다.
 * - xai: 3:2 를 직접 지원 → 3:2. 16:9 로 보내면 xAI 는 띠 대신 **잘라서(fill)** 채워 3:2 썸네일의 위아래 ~15% 가 날아간다
 *   (2026-10-16 실측 — 얼굴이 잘림). 타일·썸네일과 같은 비율이라 잘림도 띠도 없다.
 * 디스패처 `startVideoGeneration({ aspectRatio: 이 맵 })` 이 결정된 공급자로 고른다(BOOTH-71 2단계). 세션 영상(auto, 세로) 과 다르다.
 */
export const MOTION_PREVIEW_ASPECT_RATIO_BY_PROVIDER = {
    google: "16:9",
    xai: "3:2",
};
/** @deprecated 공급자별 `MOTION_PREVIEW_ASPECT_RATIO_BY_PROVIDER` 를 쓴다. Veo 값(16:9). */
export const MOTION_PREVIEW_ASPECT_RATIO = MOTION_PREVIEW_ASPECT_RATIO_BY_PROVIDER.google;
/**
 * 동작 미리보기 영상 프롬프트 — 스타일 기본 프롬프트 없이 동작 절만으로 합성한다(`composeVideoPrompt("", [동작])`).
 * 손님 영상과 같은 템플릿을 쓰므로 미리보기가 실제 결과의 동작과 어긋나지 않는다. 절이 비면 "".
 */
export function motionPreviewPrompt(motionPrompt) {
    return composeVideoPrompt("", [{ prompt: motionPrompt }]);
}
/** 동작 절 정리 — 앞뒤 공백·끝 구두점 제거. 빈 문자열이면 "". `video-reference.ts` 에서 재사용한다 (BOOTH-101). */
export function cleanClause(text) {
    return text.trim().replace(/[.。!！\s]+$/u, "").trim();
}
const SEQUENCE_WORDS = ["First", "Then", "Next", "Finally"];
/**
 * 최종 Veo 프롬프트 합성. `base` 는 스타일의 `videoPrompt`(장면·분위기), `motions` 는 고른 순서대로의 동작.
 * - 동작 0개 → base 그대로 (도입 전 동작과 동일).
 * - 동작 1개 → `${base}\n\nAnimate the subject in one continuous shot: ${m1}.`
 * - 동작 2개↑ → `${base}\n\nAnimate the subject in one continuous shot. First, m1. Then, m2.` (3개 이상은 Next…/Finally)
 * - base 가 비면 "Animate the person in the still photo …" 로 시작.
 * - base·동작 모두 비면 "" — 호출자가 "영상 프롬프트 없음" 으로 거부한다.
 */
export function composeVideoPrompt(basePrompt, motions) {
    const base = (basePrompt ?? "").trim();
    const clauses = motions.map((m) => cleanClause(m.prompt)).filter((c) => c.length > 0);
    if (clauses.length === 0)
        return base;
    const subject = base ? "Animate the subject in one continuous shot" : "Animate the person in the still photo in one continuous shot";
    let sequence;
    if (clauses.length === 1) {
        sequence = `${subject}: ${clauses[0]}.`;
    }
    else {
        const steps = clauses.map((c, i) => {
            const word = i === 0 ? SEQUENCE_WORDS[0] : i === clauses.length - 1 ? SEQUENCE_WORDS[3] : i === 1 ? SEQUENCE_WORDS[1] : SEQUENCE_WORDS[2];
            // 2개일 때는 "First, … Then, …" — Finally 는 3개 이상에서만
            const w = clauses.length === 2 && i === 1 ? SEQUENCE_WORDS[1] : word;
            return `${w}, ${c}.`;
        });
        sequence = `${subject}. ${steps.join(" ")}`;
    }
    return base ? `${base}\n\n${sequence}` : sequence;
}
/**
 * 동작 선택 토글 — 이미 있으면 제거(나머지 순서 유지), 없고 `max` 미만이면 뒤에 추가, 꽉 차면 그대로.
 * 키오스크 타일과 콘솔 테스트 다이얼로그가 같은 규칙을 쓴다. 입력 배열은 변경하지 않는다.
 */
export function toggleMotionPick(selected, id, max = MOTION_PICK_MAX) {
    if (selected.includes(id))
        return selected.filter((s) => s !== id);
    if (selected.length >= max)
        return [...selected];
    return [...selected, id];
}
/** 기본 동작 6종 — 테마 무관 범용. "도채비 등장" 처럼 테마 전용 동작은 운영자가 콘솔에서 추가한다. */
export const MOCK_VIDEO_MOTIONS = [
    {
        id: "motion_wind",
        name: "바람에 옷자락",
        nameEn: "Wind in the Clothes",
        nameJa: "風に揺れる裾",
        nameZh: "衣袂随风",
        prompt: "a gentle breeze lifts the hem and sleeves of the outfit while the hair sways softly",
        thumbnailUrl: "",
        sortOrder: 10,
        isEnabled: true,
    },
    {
        id: "motion_turn_smile",
        name: "돌아보며 웃기",
        nameEn: "Turn and Smile",
        nameJa: "振り向いて微笑む",
        nameZh: "回眸一笑",
        prompt: "the subject slowly turns the head toward the camera and breaks into a warm smile",
        thumbnailUrl: "",
        sortOrder: 20,
        isEnabled: true,
    },
    {
        id: "motion_light_scatter",
        name: "빛 흩어짐",
        nameEn: "Scattering Light",
        nameJa: "光が舞う",
        nameZh: "光影流散",
        prompt: "soft particles of light drift across the frame and scatter around the subject",
        thumbnailUrl: "",
        sortOrder: 30,
        isEnabled: true,
    },
    {
        id: "motion_reach_hand",
        name: "손 내밀기",
        nameEn: "Reaching Out",
        nameJa: "手を差し伸べる",
        nameZh: "伸出手",
        prompt: "the subject gently extends one hand toward the viewer, palm open, as if inviting them in",
        thumbnailUrl: "",
        sortOrder: 40,
        isEnabled: true,
    },
    {
        id: "motion_step_forward",
        name: "천천히 다가오기",
        nameEn: "Slow Approach",
        nameJa: "ゆっくり近づく",
        nameZh: "缓缓走近",
        prompt: "the camera eases forward as the subject takes a slow, graceful step toward the viewer",
        thumbnailUrl: "",
        sortOrder: 50,
        isEnabled: true,
    },
    {
        id: "motion_vanish",
        name: "사라지기",
        nameEn: "Fade Away",
        nameJa: "消えていく",
        nameZh: "渐渐消失",
        prompt: "the subject dissolves into drifting motes of light and gently fades from the scene",
        thumbnailUrl: "",
        sortOrder: 60,
        isEnabled: true,
    },
];
//# sourceMappingURL=video-motion.js.map